import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { FiCopy, FiShare2, FiDownload, FiEye, FiLink } from "react-icons/fi";
import api from "../api";
import "./style/LoginPromptModal.css";
import "./style/AlbumModal.css";
import "./style/AlbumShareModal.css";

const showToast = (text) => {
  const toast = document.createElement("div");
  toast.textContent = text;
  Object.assign(toast.style, {
    position: "fixed", bottom: "24px", left: "50%", transform: "translateX(-50%)",
    background: "#262626", color: "#fff", padding: "10px 20px", borderRadius: "8px",
    fontSize: "14px", zIndex: 9999, pointerEvents: "none",
  });
  document.body.appendChild(toast);
  setTimeout(() => document.body.removeChild(toast), 2800);
};

const MODES = [
  { key: "download", allowDownload: true, icon: FiDownload, title: "album_share.download_title", desc: "album_share.download_desc" },
  { key: "viewonly", allowDownload: false, icon: FiEye, title: "album_share.viewonly_title", desc: "album_share.viewonly_desc" },
];

const AlbumShareModal = ({ album, onClose }) => {
  const { t } = useTranslation();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyMode, setBusyMode] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!album) return;
    setLoading(true);
    setError(null);
    api
      .get(`/albums/${album.album_id}/shares`, { withCredentials: true })
      .then((res) => setShares(res.data?.shares || []))
      .catch(() => setError(t("album_share.error")))
      .finally(() => setLoading(false));
  }, [album]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!album) return null;

  const shareFor = (allowDownload) => shares.find((s) => s.allow_download === allowDownload);

  const handleCreate = async (mode) => {
    setBusyMode(mode.key);
    setError(null);
    try {
      const res = await api.post(
        `/albums/${album.album_id}/shares`,
        { allow_download: mode.allowDownload },
        { withCredentials: true }
      );
      const share = res.data.share;
      setShares((prev) => [...prev.filter((s) => s.allow_download !== mode.allowDownload), share]);
      ReactGA.event({ category: "engagement", action: "album_share_create", label: mode.key });
    } catch {
      setError(t("album_share.error"));
    } finally {
      setBusyMode(null);
    }
  };

  const handleDisable = async (mode, share) => {
    if (!window.confirm(t("album_share.confirm_disable"))) return;
    setBusyMode(mode.key);
    setError(null);
    try {
      await api.delete(`/albums/${album.album_id}/shares/${share.token}`, { withCredentials: true });
      setShares((prev) => prev.filter((s) => s.token !== share.token));
    } catch {
      setError(t("album_share.error"));
    } finally {
      setBusyMode(null);
    }
  };

  const handleCopy = (share, mode) => {
    navigator.clipboard.writeText(share.url).then(() => showToast(t("album_share.copied"))).catch(() => {});
    ReactGA.event({ category: "engagement", action: "album_share_copy", label: mode.key });
  };

  const handleNativeShare = (share, mode) => {
    navigator.share({ url: share.url, title: t("album_share.native_title", { name: album.name }) }).catch(() => {});
    ReactGA.event({ category: "engagement", action: "album_share_native", label: mode.key });
  };

  return (
    <div className="lpm-overlay" onClick={onClose}>
      <div className="am-card" onClick={(e) => e.stopPropagation()}>
        <div className="am-header">
          <h2 className="am-title">{t("album_share.title", { name: album.name })}</h2>
          <button className="am-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className="asm-subtitle">{t("album_share.subtitle")}</p>

        <div className="am-body asm-body">
          {error && <p className="am-error">{error}</p>}
          {loading ? (
            <div className="am-loading"><div className="spinner-border spinner-border-sm text-secondary" /></div>
          ) : (
            MODES.map((mode) => {
              const share = shareFor(mode.allowDownload);
              const busy = busyMode === mode.key;
              const Icon = mode.icon;
              return (
                <div key={mode.key} className="asm-row">
                  <div className="asm-row-head">
                    <span className="asm-row-icon"><Icon /></span>
                    <div className="asm-row-text">
                      <span className="asm-row-title">{t(mode.title)}</span>
                      <span className="asm-row-desc">{t(mode.desc)}</span>
                    </div>
                    {!share && (
                      <button className="asm-btn asm-btn--primary" onClick={() => handleCreate(mode)} disabled={busy}>
                        <FiLink /> {t("album_share.create")}
                      </button>
                    )}
                  </div>

                  {share && (
                    <>
                      <div className="asm-link">
                        <input className="asm-link-input" value={share.url} readOnly onFocus={(e) => e.target.select()} />
                        <button className="asm-btn asm-btn--primary" onClick={() => handleCopy(share, mode)}>
                          <FiCopy /> {t("album_share.copy")}
                        </button>
                        {navigator.share && (
                          <button className="am-icon-btn" onClick={() => handleNativeShare(share, mode)} title={t("album_share.share")}>
                            <FiShare2 />
                          </button>
                        )}
                      </div>
                      <button className="asm-disable" onClick={() => handleDisable(mode, share)} disabled={busy}>
                        {t("album_share.disable")}
                      </button>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AlbumShareModal;
